import React, { useContext, useState } from "react";
import styles from "../../styles/cr.module.scss";
import { RagIndicator } from "../cr/RagIndicator";
import UpdateRagDisplay from "./UpdateRagDisplay";
import { RagColours } from "../../refData/RagColours";
import { IBaseComponentProps, IReportDueDates, IReportingEntity, ISignOff, ListDefaults, ProgrammeUpdate } from "../../types";
import { DateService } from "../../services";
import { getUpdateReportingStage, UpdateReportingStages } from "../../refData/UpdateReportingStages";
import { Panel, PanelType } from "@fluentui/react/lib/Panel";
import { IconButton } from "@fluentui/react/lib/Button";
import { OrbUserContext } from "../OrbUserContext";
import { EntityStatus } from "../../refData/EntityStatus";
import { ProgrammeProgressUpdateForm } from "../programme/ProgrammeProgressUpdateForm";


interface IProgrammeUpdatesAndSignOff extends IBaseComponentProps {
    reportDates: IReportDueDates;
    report: Partial<ISignOff>;
    previousReport: ISignOff;
    preHeadingText?: string;
    loadReport: () => void;
    approvedByText?: string;
}

const ProgrammeUpdatesAndSignOff = (props: IProgrammeUpdatesAndSignOff) => {

    const { userPermissions } = useContext(OrbUserContext);
    const [showForm, setShowForm] = useState(false);
    const programme = props.report?.Programme;
    const programmeId = programme?.ID;
    const latestUpdates = programme?.ProgrammeUpdates?.[0];
    const pu = latestUpdates || new ProgrammeUpdate();

    // read-only logic
    const readOnly =
        (props.report?.Programme?.ProgrammeUpdates?.[0]?.ReportingStageID ?? 0) >
        UpdateReportingStages.UpdatesInProgress;

    const hideEdit = !!readOnly;

    // edit button
    const editButton = (
        programmeId &&
        !hideEdit &&
        userPermissions.UserCanSubmitProgrammeUpdates(programmeId) && (
            <IconButton
                iconProps={{ iconName: "Edit" }}
                title="Edit"
                onClick={() => setShowForm(true)}
            />
        )
    );


    const renderDetailsSection = () => {

        const programme = props.report?.Programme;

        const programmeTitle = programme?.Title;
        const updatedBy = programme?.ProgrammeUpdates?.[0]?.UpdateUser?.Title;
        const approvedBy = props.approvedByText;
        const period = DateService.dateToUkLongDate(props.reportDates?.Next);
        const stage = getUpdateReportingStage(programme?.ProgrammeUpdates?.[0]?.ReportingStageID);
        const objectives = programme?.Objectives;

        const parentElement = (() => {
            if (!programme) return null;

            const parentprogrammeTitle = programme.Portfolio?.Title || "";
            return parentprogrammeTitle;
        })();



        return (
            <div>
                {/* Section heading */}
                <div style={{ marginBottom: "10px", marginTop: "30px" }}>
                    <div style={{ display: "flex" }}>
                        <div className={styles.sectionATitle}>Details</div>
                    </div>
                </div>

                {/* Table */}
                <div className="info-table-container">
                    <table className="info-table" cellSpacing={0} cellPadding={10}>
                        <tbody>
                            <tr>
                                <td className="label">Programme</td>
                                <td className="value">{programmeTitle}</td>
                                <td className="label">Parent</td>
                                <td className="value">{parentElement}</td>
                            </tr>
                            <tr>
                                <td className="label">Period</td>
                                <td className="value">{period}</td>
                                <td className="label">Status</td>
                                <td className="value">{stage}</td>
                            </tr>
                            <tr>
                                <td className="label">Updated By</td>
                                <td className="value">{updatedBy}</td>
                                <td className="label">Approved By</td>
                                <td className="value">{approvedBy}</td>
                            </tr>
                            <tr>
                                <td style={{ verticalAlign: 'top' }} className="label">Programme Objectives</td>
                                <td className="value" colSpan={3}>
                                    {objectives}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };


    // Render headlines section
    const renderHeadlinesSection = () => {
        const programme = props.report?.Programme;
        const latestUpdates = programme?.ProgrammeUpdates?.[0];
        const pu = latestUpdates || new ProgrammeUpdate();

        // Calculations / mappings
        const deliveryConfidenceRag = pu.OverallRagOptionID;
        const deliveryConfidenceDescription = pu.ProgressUpdate || ListDefaults.placeholders.dataTBC;
        const furtherActions = pu.FutureActions || ListDefaults.placeholders.dataTBC;
        const escalations = pu.Escalations || ListDefaults.placeholders.dataTBC;

        return (
            <div>
                {/* Section heading */}
                <div style={{ marginBottom: "10px", marginTop: "30px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div className={styles.sectionATitle}>Headlines</div>
                        {editButton}
                    </div>
                </div>

                {/* Table */}
                <div className="info-table-container">
                    <table className="info-table" cellSpacing={0} cellPadding={10}>
                        <tbody>
                            <tr className="invisible-row">
                                <td className="label"></td>
                                <td className="value"></td>
                                <td className="label"></td>
                                <td className="value"></td>
                            </tr>

                            <tr className="first-visible-row">
                                <td className="label">Delivery Confidence Rating</td>
                                <td className="value" colSpan={3}>
                                    <UpdateRagDisplay rag={deliveryConfidenceRag} />
                                </td>
                            </tr>

                            <tr>
                                <td className="label">Delivery Confidence Description</td>
                                <td className="value" colSpan={3}>
                                    {deliveryConfidenceDescription}
                                </td>
                            </tr>

                            <tr>
                                <td className="label">Further Actions</td>
                                <td className="value" colSpan={3}>
                                    {furtherActions}
                                </td>
                            </tr>
                            <tr>
                                <td className="label">Escalations for senior leader action</td>
                                <td className="value" colSpan={3}>
                                    {escalations}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };


    // Render key indicators section
    const renderKeyIndicatorsSection = () => {
        // Calculations / mappings
        const financeRag = pu.FinanceRagOptionID;
        const financeDetails = pu.FinanceComment || ListDefaults.placeholders.dataTBC;

        const milestonesRag = pu.MilestonesRagOptionID;
        const milestonesDetails = pu.MilestonesComment || ListDefaults.placeholders.dataTBC;

        const peopleRag = pu.PeopleRagOptionID;
        const peopleDetails = pu.PeopleComment || ListDefaults.placeholders.dataTBC;

        const benefitsRag = pu.BenefitsRagOptionID;
        const benefitsDetails = pu.BenefitsComment || ListDefaults.placeholders.dataTBC;

        return (
            <div>
                {/* Section heading */}
                <div style={{ marginBottom: "10px", marginTop: "30px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div className={styles.sectionATitle}>Key Indicators</div>
                        {editButton}
                    </div>
                </div>

                {/* Table */}
                <div className="info-table-container">
                    <table className="info-table" cellSpacing={0} cellPadding={10}>
                        <tbody>
                            <tr>
                                <td className="label">Finance Rating</td>
                                <td className="value" colSpan={3}>
                                    <UpdateRagDisplay rag={financeRag} />
                                </td>
                            </tr>
                            <tr>
                                <td className="label" style={{ verticalAlign: "top" }}>Finance Details</td>
                                <td className="value" colSpan={3}>
                                    <div style={{ minHeight: "100px", width: "100%", boxSizing: "border-box" }}>
                                        {financeDetails}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="label">People Rating</td>
                                <td className="value" colSpan={3}>
                                    <UpdateRagDisplay rag={peopleRag} />
                                </td>
                            </tr>
                            <tr>
                                <td className="label" style={{ verticalAlign: "top" }}>People Details</td>
                                <td className="value" colSpan={3}>
                                    <div style={{ minHeight: "100px", width: "100%", boxSizing: "border-box" }}>
                                        {peopleDetails}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="label">Milestones Rating</td>
                                <td className="value" colSpan={3}>
                                    <UpdateRagDisplay rag={milestonesRag} />
                                </td>
                            </tr>
                            <tr>
                                <td className="label" style={{ verticalAlign: "top" }}>Milestones Details</td>
                                <td className="value" colSpan={3}>
                                    <div style={{ minHeight: "100px", width: "100%", boxSizing: "border-box" }}>
                                        {milestonesDetails}
                                    </div>
                                </td>
                            </tr>
                            <tr>
                                <td className="label">Benefits Rating</td>
                                <td className="value" colSpan={3}>
                                    <UpdateRagDisplay rag={benefitsRag} />
                                </td>
                            </tr>
                            <tr>
                                <td className="label" style={{ verticalAlign: "top" }}>Benefits Details</td>
                                <td className="value" colSpan={3}>
                                    <div style={{ minHeight: "100px", width: "100%", boxSizing: "border-box" }}>
                                        {benefitsDetails}
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };


    const closeEditUpdate = (reloadList: boolean): void => {
        setShowForm(false);
        if (reloadList) props.loadReport?.();
    };

    const isOpen = (entity: IReportingEntity, updatesProp: string): boolean => entity.EntityStatusID === EntityStatus.Open || entity[updatesProp]?.length > 0;

    return (
        <div className={styles.cr} style={{ width: '99%', minHeight: '400px', position: 'relative' }}>
            {/* Main Heading */}
            <h1 style={{ fontWeight: '500' }} className={styles.headings2}>
                Programme Updates
            </h1>

            {/* Details Section */}
            {renderDetailsSection()}

            {/* Headlines Section */}
            {renderHeadlinesSection()}

            {/* Indicators Section */}
            {renderKeyIndicatorsSection()}

            <Panel isOpen={showForm} headerText="Edit programme update" type={PanelType.medium} onDismiss={() => setShowForm(false)}>
                <ProgrammeProgressUpdateForm
                    {...props}
                    entityId={programmeId}
                    entityUpdateId={pu?.ID}
                    defaultShowForm={true}
                    onSaved={() => closeEditUpdate(true)}
                    onCancelled={() => closeEditUpdate(false)}
                />
            </Panel>

        </div>
    );
};

export default ProgrammeUpdatesAndSignOff;
